import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CustomButton, CustomIconButton, icon, InfiniteList2 } from "../../../../common/components";
import { useApi, usePrevious } from "../../../../common/hooks";
import { NotesService } from "../../../../api";
import { translate } from "../../../../common/providers";
import { setIsGenerating, setSelectedNote } from "../../slice/note/note-slice";
import styles from "./NotesSidePanel.module.css";
import NoteRow from "./components/note-row/NoteRow";
import ManualCreationDialog from "../right-side-panel/components/note-creation-panel/manual-creation-dialog/ManualCreationDialog";
import AiCreationDialog from "../right-side-panel/components/ai-creation-dialog/AICreationDialog";

const NotesSidePanel = ({ isPanelOpen, noteStatus, onSetNoteStatus, onTogglePanel, onCancelForceNoteCreation }) => {
	const { call: getAllNotes } = useApi(NotesService.getAllNotes);
	const { call: create } = useApi(NotesService.create);
	const { call: saveNoteAgentConfig } = useApi(NotesService.saveNoteAgentConfig);
	const { call: createAI } = useApi(NotesService.createAI);
	const { call: updateNoteStatus } = useApi(NotesService.updateNoteStatus);
	const projectId = useSelector(({ context }) => context.project.id);
	const isGenerating = useSelector(({ note }) => note.isGenerating);
	const previousIsGenerating = usePrevious(isGenerating);
	const userId = useSelector(({ context }) => context.user?.id);
	const noteIdRedux = useSelector(({ note }) => note.selectedNote?.id);
	const [sort] = useState({ sort: "LAST_MODIFICATION", dir: "DESC" });
	const [openAiConfigPanel, setOpenAiConfigPanel] = useState(false);
	const [request, setRequest] = useState(null);
	const tempManualNoteBtn = false;
	const [openManualCreationDialog, setOpenManualCreationDialog] = useState(false);
	const dispatch = useDispatch();

	const buildRequest = useCallback(() => {
		setRequest(
			() =>
				({ page, limit }) =>
					getAllNotes({ projectId }, { page, limit, ...sort }, {})
		);
	}, [getAllNotes, projectId, sort]);

	useEffect(() => {
		buildRequest();
	}, [buildRequest]);

	useEffect(() => {
		if (previousIsGenerating && !isGenerating) {
			buildRequest();
		}
	}, [previousIsGenerating, isGenerating, buildRequest]);

	const initNoteCreationAI = () => {
		if (isGenerating) {
			return;
		}
		setOpenAiConfigPanel(true);
	};
	const handleCloseAiConfig = () => {
		setOpenAiConfigPanel(false);
	};

	const handleLaunchNoteCreation = ({
		integrationPolicy,
		documents,
		additionPrompt,
		selectDocumentAutomatically,
	}) => {
		saveNoteAgentConfig(
			{ projectId },
			{ integrationPolicy, documents, additionPrompt, selectDocumentAutomatically }
		).then(() => {
			createAI({ projectId });
			handleCloseAiConfig();
			dispatch(setIsGenerating(true));
		});
	};
	const handleSelectNote = (note) => {
		const { id, title, generatedByAI, lastModified, elapsed, lastModifiedBy, lastModifiedById, status } = note;
		if (noteIdRedux && noteStatus && noteStatus.status === "LOCKED" && noteStatus.userId === userId) {
			updateNoteStatus({ noteId: noteIdRedux }, { status: "OPENED" })
				.then(() => {
					dispatch(setSelectedNote({ id, title, generatedByAI, lastModified, elapsed, lastModifiedBy }));
					if (status === "OPENED") {
						updateNoteStatus({ noteId: id }, { status: "LOCKED" })
							.then(() => {
								onSetNoteStatus({ noteId: id, userId, status: "LOCKED" });
							})
							.catch(console.error);
					} else {
						onSetNoteStatus({ noteId: id, userId: lastModifiedById, status: "LOCKED" });
					}
				})
				.catch(console.error);
		} else {
			dispatch(setSelectedNote({ id, title, generatedByAI, lastModified, elapsed, lastModifiedBy }));
			if (status === "OPENED") {
				updateNoteStatus({ noteId: id }, { status: "LOCKED" })
					.then(() => {
						onSetNoteStatus({ noteId: id, userId, status: "LOCKED" });
					})
					.catch(console.error);
			} else {
				onSetNoteStatus({ noteId: id, userId: lastModifiedById, status: "LOCKED" });
			}
		}
	};

	const rowRenderer = (note) => <NoteRow key={note.id} note={note} onClick={handleSelectNote} />;
	const initManualNoteCreationEmpty = () => {
		setOpenManualCreationDialog(true);
	};
	const handleCloseManualCreationDialog = () => {
		setOpenManualCreationDialog(false);
	};
	const handleCreateNote = (value) => {
		create({ projectId }, { value })
			.then((data) => {
				dispatch(
					setSelectedNote({
						id: data.id,
						title: data.title,
						elapsed: data.elapsed,
						lastModified: data.lastModified,
						lastModifiedBy: data.lastModifiedBy,
					})
				);
				if (data.status === "OPENED") {
					updateNoteStatus({ noteId: data.id }, { status: "LOCKED" })
						.then(() => {
							onSetNoteStatus({ noteId: data.id, userId, status: "LOCKED" });
						})
						.catch(console.error);
				} else {
					onSetNoteStatus({ noteId: data.id, userId: data.lastModifiedById, status: "LOCKED" });
				}
				handleCloseManualCreationDialog();
				onCancelForceNoteCreation();
			})
			.catch(console.error);
	};

	return (
		<>
			<div className={styles.panel} data-open={isPanelOpen}>
				<div className={styles.header}>
					{isPanelOpen ? (
						<>
							<span>
								<CustomButton
									color="primary"
									disabled={isGenerating}
									startIcon={icon.faPlus}
									variant="contained"
									onClick={initNoteCreationAI}
								>
									{translate("notes.right-panel.empty-state.creation.btn.ai.title")}
								</CustomButton>
								{tempManualNoteBtn && (
									<CustomButton
										color="secondary"
										startIcon={icon.faPlus}
										variant="outlined"
										onClick={initManualNoteCreationEmpty}
									>
										{translate("notes.right-panel.empty-state.creation.btn.manual.title")}
									</CustomButton>
								)}
							</span>
							<CustomIconButton
								color="secondary"
								icon={icon.faAngleDoubleLeft}
								size="sm"
								variant="outlined"
								onClick={onTogglePanel}
							/>
						</>
					) : (
						<CustomIconButton
							color="secondary"
							icon={icon.faAngleDoubleRight}
							size="sm"
							variant="outlined"
							onClick={onTogglePanel}
						/>
					)}
				</div>
				{isPanelOpen && (
					<div className={styles.list}>
						<InfiniteList2 request={request} rowRenderer={rowRenderer} />
					</div>
				)}
			</div>
			<AiCreationDialog
				open={openAiConfigPanel}
				onClose={handleCloseAiConfig}
				onSubmit={handleLaunchNoteCreation}
			/>
			<ManualCreationDialog
				label={translate("notes.action.create.placeholder")}
				open={openManualCreationDialog}
				submitLabel={translate("common:btn.create")}
				title={translate("notes.action.create.title")}
				onClose={handleCloseManualCreationDialog}
				onSubmit={handleCreateNote}
			/>
		</>
	);
};

export default NotesSidePanel;
